import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import StoryCard from '../components/StoryCard';
import ReportButton from '../components/ReportButton';

function XpProgressBar({ xp, level }: { xp: number; level: number }) {
  // Calculate XP thresholds using same formula as backend
  const xpForLevel = (lvl: number) => lvl <= 1 ? 0 : Math.floor(50 * lvl * (lvl - 1));
  const currentLevelXp = xpForLevel(level);
  const nextLevelXp = xpForLevel(level + 1);
  const xpIntoLevel = xp - currentLevelXp;
  const xpNeeded = nextLevelXp - currentLevelXp;
  const percent = xpNeeded > 0 ? Math.min(100, Math.round((xpIntoLevel / xpNeeded) * 100)) : 100;

  return (
    <div className="mt-3 w-full max-w-xs">
      <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
        <span>Level {level}</span>
        <span>{xpIntoLevel.toLocaleString()} / {xpNeeded.toLocaleString()} XP</span>
        <span>Level {level + 1}</span>
      </div>
      <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${percent}%`,
            background: 'linear-gradient(90deg, #c4a44a, #e8c84a)',
          }}
        />
      </div>
    </div>
  );
}

interface ProfilePageProps {
  username: string;
  onNavigate: (page: string, data?: any) => void;
  isPenProfile?: boolean;
  user?: any;
}

const SOCIAL_PLATFORMS: { key: string; label: string; icon: string; urlPrefix?: string }[] = [
  { key: 'twitter', label: 'Twitter/X', icon: '𝕏', urlPrefix: 'https://x.com/' },
  { key: 'patreon', label: 'Patreon', icon: '🅿', urlPrefix: 'https://patreon.com/' },
  { key: 'instagram', label: 'Instagram', icon: '📷', urlPrefix: 'https://instagram.com/' },
  { key: 'youtube', label: 'YouTube', icon: '▶', urlPrefix: 'https://youtube.com/@' },
  { key: 'tiktok', label: 'TikTok', icon: '♪', urlPrefix: 'https://tiktok.com/@' },
  { key: 'discord', label: 'Discord', icon: '💬' },
  { key: 'website', label: 'Website', icon: '🌐' },
];

function getSocialUrl(key: string, value: string): string | null {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  const platform = SOCIAL_PLATFORMS.find(p => p.key === key);
  if (platform?.urlPrefix) return platform.urlPrefix + value;
  if (key === 'website') return 'https://' + value;
  return null;
}

function EditProfileModal({ profile, onClose, onSaved }: { profile: any; onClose: () => void; onSaved: () => void }) {
  const [bio, setBio] = useState(profile.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(profile.avatarUrl || '');
  const [socialLinks, setSocialLinks] = useState<Record<string, string>>(() => {
    const links = profile.socialLinks || {};
    const result: Record<string, string> = {};
    SOCIAL_PLATFORMS.forEach(p => { result[p.key] = links[p.key] || ''; });
    return result;
  });
  const [newFavId, setNewFavId] = useState('');
  const [favorites, setFavorites] = useState<any[]>(profile.favoriteStories || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'profile' | 'social' | 'favorites'>('profile');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const cleanLinks: Record<string, string> = {};
      Object.entries(socialLinks).forEach(([k, v]) => { if (v.trim()) cleanLinks[k] = v.trim(); });
      await api.users.updateProfile({ bio, avatarUrl, socialLinks: cleanLinks });
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddFavorite = async () => {
    const id = parseInt(newFavId);
    if (!id || isNaN(id)) return;
    try {
      await api.users.addFavorite(id);
      setNewFavId('');
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Failed to add favorite');
    }
  };

  const handleRemoveFavorite = async (storyId: number) => {
    try {
      await api.users.removeFavorite(storyId);
      setFavorites(prev => prev.filter((f: any) => (f.story?.id || f.storyId) !== storyId));
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Failed to remove favorite');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h2 className="text-lg font-bold">Edit Profile</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>

        <div className="flex border-b border-gray-800">
          {(['profile', 'social', 'favorites'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2.5 text-sm font-medium capitalize ${activeTab === tab ? 'text-ink-400 border-b-2 border-ink-400' : 'text-gray-400 hover:text-gray-200'}`}
            >
              {tab === 'social' ? 'Social Links' : tab}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {activeTab === 'profile' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Bio</label>
                <textarea
                  value={bio}
                  onChange={e => { if (e.target.value.length <= 500) setBio(e.target.value); }}
                  rows={4}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-ink-400"
                  placeholder="Tell us about yourself..."
                />
                <div className="text-xs text-gray-500 text-right mt-1">{bio.length}/500</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Avatar URL</label>
                <input
                  type="text"
                  value={avatarUrl}
                  onChange={e => setAvatarUrl(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-ink-400"
                  placeholder="https://example.com/avatar.png"
                />
              </div>
            </>
          )}

          {activeTab === 'social' && (
            <>
              {SOCIAL_PLATFORMS.map(p => (
                <div key={p.key}>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    <span className="mr-1">{p.icon}</span> {p.label}
                  </label>
                  <input
                    type="text"
                    value={socialLinks[p.key]}
                    onChange={e => setSocialLinks(prev => ({ ...prev, [p.key]: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-ink-400"
                    placeholder={p.urlPrefix ? `username or full URL` : p.key === 'discord' ? 'Discord username' : 'https://...'}
                  />
                </div>
              ))}
            </>
          )}

          {activeTab === 'favorites' && (
            <>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFavId}
                  onChange={e => setNewFavId(e.target.value)}
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-ink-400"
                  placeholder="Enter Story ID"
                />
                <button
                  onClick={handleAddFavorite}
                  className="px-4 py-2 bg-ink-500 hover:bg-ink-600 text-white text-sm rounded-lg font-medium"
                >
                  Add
                </button>
              </div>
              {favorites.length > 0 && (
                <div className="space-y-2">
                  {favorites.map((fav: any) => {
                    const story = fav.story || fav;
                    return (
                      <div key={story.id} className="flex items-center justify-between bg-gray-800 rounded-lg px-3 py-2">
                        <div className="text-sm">
                          <span className="text-white font-medium">{story.title}</span>
                          {story.genre && <span className="ml-2 text-xs text-gray-400">{story.genre}</span>}
                        </div>
                        <button
                          onClick={() => handleRemoveFavorite(story.id)}
                          className="text-red-400 hover:text-red-300 text-sm ml-2"
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-gray-800">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-ink-500 hover:bg-ink-600 disabled:opacity-50 text-white text-sm rounded-lg font-medium"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProfilePage({ username, onNavigate, isPenProfile, user }: ProfilePageProps) {
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [inkReceived, setInkReceived] = useState<{ totalReceived: number; tipCount: number } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isFollowingAuthor, setIsFollowingAuthor] = useState(false);
  const [authorFollowerCount, setAuthorFollowerCount] = useState(0);

  const handleFollowAuthor = async () => {
    if (!user) return onNavigate('login');
    try {
      const res = await api.users.followAuthor(profile.id);
      setIsFollowingAuthor(res.following);
      setAuthorFollowerCount((c: number) => c + (res.following ? 1 : -1));
    } catch (err) {
      console.error('Follow author failed:', err);
    }
  };

  const fetchProfile = () => {
    setLoading(true);
    setProfile(null);
    setInkReceived(null);

    if (isPenProfile) {
      api.penNames.profile(username)
        .then(setProfile)
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      api.users.profile(username)
        .then(data => {
          setProfile(data);
          setIsFollowingAuthor(data.isFollowingAuthor || false);
          setAuthorFollowerCount(data._count?.authorFollows || 0);
          if (data.role === 'AUTHOR') {
            api.ink.received(data.id).then(setInkReceived).catch(() => {});
          }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [username, isPenProfile]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) return <div className="text-center py-32 text-gray-400">{isPenProfile ? 'Pen name not found' : 'User not found'}</div>;

  if (isPenProfile) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-start gap-6 mb-10">
          <div className="w-20 h-20 rounded-full bg-ink-600 flex items-center justify-center text-3xl font-bold flex-shrink-0">
            {profile.avatarUrl ? (
              <img src={profile.avatarUrl} alt={profile.penName} className="w-full h-full rounded-full object-cover" />
            ) : (
              profile.penName[0].toUpperCase()
            )}
          </div>
          <div>
            <h1 className="text-2xl font-bold">{profile.penName}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-ink-600/20 text-ink-400">
                Author
              </span>
            </div>
            {profile.bio && <p className="text-gray-300 mt-3">{profile.bio}</p>}
            <p className="text-sm text-gray-500 mt-2">
              Writing since {new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

        {profile.stories?.length > 0 && (
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4">Stories ({profile.stories.length})</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {profile.stories.map((story: any) => (
                <StoryCard
                  key={story.id}
                  story={{ ...story, penName: { penName: profile.penName }, author: { username: profile.penName } }}
                  onClick={() => onNavigate('story', { id: story.id, slug: story.slug })}
                  onTagClick={(tag) => onNavigate('discover', { tag })}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    );
  }

  const isOwnProfile = user && profile && user.id === profile.id;
  const socialLinks = profile.socialLinks || {};
  const hasSocialLinks = Object.values(socialLinks).some((v: any) => v && v.trim());
  const favoriteStories = profile.favoriteStories || [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-start gap-6 mb-10">
        <div className="w-20 h-20 rounded-full bg-ink-600 flex items-center justify-center text-3xl font-bold flex-shrink-0">
          {profile.avatarUrl ? (
            <img src={profile.avatarUrl} alt={profile.username} className="w-full h-full rounded-full object-cover" />
          ) : (
            profile.username[0].toUpperCase()
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{profile.username}</h1>
            {isOwnProfile && (
              <button
                onClick={() => setShowEditModal(true)}
                className="px-3 py-1 text-xs font-medium bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-lg transition-colors"
              >
                Edit Profile
              </button>
            )}
            {user && !isOwnProfile && (
              <ReportButton targetType="USER" targetId={profile.id} targetLabel={profile.username} variant="text" className="text-xs" />
            )}
            {user && !isOwnProfile && (
              <button
                onClick={() => onNavigate('messages', { username: profile.username })}
                className="px-3 py-1 text-xs font-medium bg-ink-600 hover:bg-ink-500 text-white rounded-lg transition-colors flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Message
              </button>
            )}
            {!isOwnProfile && (
              <button
                onClick={handleFollowAuthor}
                className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors border ${
                  isFollowingAuthor
                    ? 'bg-ink-600/20 border-ink-500 text-ink-400'
                    : 'bg-gray-800 border-gray-700 hover:border-ink-500 text-gray-300'
                }`}
              >
                {isFollowingAuthor ? '✓ Following Author' : '+ Follow Author'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              profile.role === 'AUTHOR' ? 'bg-ink-600/20 text-ink-400' : 'bg-gray-800 text-gray-400'
            }`}>
              {profile.role}
            </span>
            <span>Level {profile.level}</span>
            <span>{profile.xp.toLocaleString()} XP</span>
            {profile.readingStreak > 0 && <span>{profile.readingStreak} day streak</span>}
            {authorFollowerCount > 0 && (
              <span>{authorFollowerCount.toLocaleString()} author {authorFollowerCount === 1 ? 'follower' : 'followers'}</span>
            )}
            {inkReceived && inkReceived.totalReceived > 0 && (
              <span className="text-amber-400">{inkReceived.totalReceived.toLocaleString()} Ink received</span>
            )}
          </div>
          {profile.bio && <p className="text-gray-300 mt-3">{profile.bio}</p>}

          {hasSocialLinks && (
            <div className="flex flex-wrap gap-2 mt-3">
              {SOCIAL_PLATFORMS.map(p => {
                const val = socialLinks[p.key];
                if (!val || !val.trim()) return null;
                const url = getSocialUrl(p.key, val);
                return url ? (
                  <a
                    key={p.key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-xs text-gray-300 hover:text-white transition-colors"
                  >
                    <span>{p.icon}</span>
                    <span>{p.label}</span>
                  </a>
                ) : (
                  <span
                    key={p.key}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-800 border border-gray-700 rounded-full text-xs text-gray-400"
                  >
                    <span>{p.icon}</span>
                    <span>{val}</span>
                  </span>
                );
              })}
            </div>
          )}

          <XpProgressBar xp={profile.xp} level={profile.level} />

          <p className="text-sm text-gray-500 mt-2">
            Joined {new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      {favoriteStories.length > 0 && (
        <section className="mb-10">
          <h2 className="text-lg font-bold mb-4">Favorite Stories</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {favoriteStories.slice(0, 6).map((fav: any) => {
              const story = fav.story || fav;
              return (
                <button
                  key={story.id}
                  onClick={() => onNavigate('story', { id: story.id, slug: story.slug })}
                  className="flex items-center gap-3 bg-gray-900 border border-gray-800 hover:border-gray-600 rounded-xl p-3 text-left transition-colors group"
                >
                  <div className="w-14 h-20 rounded-lg flex-shrink-0 overflow-hidden">
                    {story.coverUrl ? (
                      <img src={story.coverUrl} alt={story.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-ink-600 to-purple-700 flex items-center justify-center text-white text-lg font-bold">
                        {story.title?.[0] || '?'}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-white group-hover:text-ink-400 truncate transition-colors">{story.title}</h3>
                    {story.genre && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-ink-600/20 text-ink-400 rounded text-xs">
                        {story.genre}
                      </span>
                    )}
                    {(story.author?.username || story.penName?.penName) && (
                      <p className="text-xs text-gray-500 mt-1 truncate">by {story.penName?.penName || story.author?.username}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {profile.badges?.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-bold mb-4">Badges ({profile.badges.length})</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {profile.badges.map((b: any) => (
              <div
                key={b.id}
                className="bg-gray-900 border border-gray-800 rounded-xl p-3 text-center hover:border-amber-700/50 transition-colors group"
                title={b.description || b.badge}
              >
                <div className="text-2xl mb-1 group-hover:scale-110 transition-transform">
                  {b.icon || '\u2B50'}
                </div>
                <div className="text-sm font-medium text-amber-400">{b.name || b.badge}</div>
                <div className="text-xs text-gray-500 mt-0.5">{b.description || ''}</div>
                <div className="text-xs text-gray-600 mt-1">
                  {new Date(b.earnedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {profile.stories?.length > 0 && (
        <section className="mb-10">
          <h2 className="text-lg font-bold mb-4">Stories ({profile.stories.length})</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {profile.stories.map((story: any) => (
              <StoryCard
                key={story.id}
                story={{ ...story, author: { username: profile.username } }}
                onClick={() => onNavigate('story', { id: story.id, slug: story.slug })}
                onTagClick={(tag) => onNavigate('discover', { tag })}
              />
            ))}
          </div>
        </section>
      )}

      {profile.reviews?.length > 0 && (
        <section>
          <h2 className="text-lg font-bold mb-4">Recent Reviews</h2>
          <div className="space-y-3">
            {profile.reviews.map((review: any) => (
              <div key={review.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <button
                    onClick={() => onNavigate('story', { id: review.story.id, slug: review.story.slug })}
                    className="font-medium text-ink-400 hover:underline"
                  >
                    {review.story.title}
                  </button>
                  <span className="text-amber-400 text-sm">{'★'.repeat(Math.round(review.rating))}</span>
                </div>
                <p className="text-gray-300 text-sm">{review.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {showEditModal && createPortal(
        <EditProfileModal
          profile={profile}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false);
            fetchProfile();
          }}
        />,
        document.body
      )}
    </div>
  );
}
